import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Button,
  IconButton,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  IconRefresh,
  IconPlus,
  IconPencil,
  IconTrash,
  IconAlertTriangle,
  IconChevronDown,
  IconLink,
} from '@tabler/icons-react';
import type { ProductMapping, VendorProduct } from '@techgitter/contracts';
import { getProductMappings, deleteProductMapping } from '../../services/productMappingService';
import { getVendorProducts } from '../../services/vendorProductService';
import { ProductMappingDialog } from './ProductMappingDialog';
import { VendorProductDrillDownDialog } from './VendorProductDrillDownDialog';
import {
  SecondaryToolbar,
  ToolbarSelect,
  ToolbarSearch,
  ToolbarCheckbox,
  ToolbarSpacer,
  ToolbarButton,
} from '../toolbar';
import {
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
  DataTableEmpty,
} from '../table';
import { Chip } from '../chip';
import { CARD_SX, OUTLINED_BTN_SX, ACCENT_LINK_SX } from '../../styles/designSystem';

const ACTION_BTN_SX = { ...OUTLINED_BTN_SX, textTransform: 'none', fontSize: 12, py: 0.25, px: 1, minWidth: 0 };
const LINK_BTN_SX = {
  ...ACCENT_LINK_SX,
  textTransform: 'none',
  p: 0,
  minWidth: 0,
  fontSize: 13,
  fontWeight: 500,
  justifyContent: 'flex-start',
  '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
};

interface Props {
  /** Admin gate — create/edit/delete actions only render when true. */
  canEdit: boolean;
  /**
   * Shared Product filter (page-level, persists across tabs). Mappings are
   * commercial ⇄ vendor-product bridges with no customer dimension, so there's
   * no customer filter here; this drives the panel's own product search box.
   */
  productFilter?: string;
  onProductFilterChange?: (v: string) => void;
}

/**
 * The Mappings area: review and manage `cr001_product_mappings` (the commercial
 * ⇄ vendor product bridge feeding billing coverage). Bad mappings — term
 * mismatches, duplicates, wrong product — are easy to create and hard to spot,
 * so this surfaces them with filters and a term-mismatch warning. Admin-gated
 * create/edit/delete.
 */
export const MappingsPanel: React.FC<Props> = ({
  canEdit,
  productFilter = '',
  onProductFilterChange,
}) => {
  const [mappings, setMappings] = useState<ProductMapping[]>([]);
  const [vendorProducts, setVendorProducts] = useState<VendorProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [vendorFilter, setVendorFilter] = useState<string>('all');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [unmappedOpen, setUnmappedOpen] = useState(false);

  const [dialogTarget, setDialogTarget] = useState<ProductMapping | null | undefined>(undefined);
  // Pre-selected vendor product when creating from the unmapped list.
  const [dialogInitialVp, setDialogInitialVp] = useState<VendorProduct | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ProductMapping | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [drillTarget, setDrillTarget] = useState<VendorProduct | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      setLoading(true);
      setError(null);
      try {
        const [maps, vps] = await Promise.all([
          getProductMappings({ includeInactive }, signal),
          getVendorProducts(undefined, signal),
        ]);
        if (signal?.aborted) return;
        setMappings(maps);
        setVendorProducts(vps);
      } catch (e) {
        if (e instanceof Error && e.name === 'AbortError') return;
        setError(e instanceof Error ? e.message : 'Failed to load mappings');
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [includeInactive],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  // vendorProductId → owning vendor, for the "by vendor" filter + a vendor
  // column (the mapping row hydrates the vendor product but not its vendor).
  const vendorByVendorProduct = useMemo(() => {
    const m = new Map<string, { vendorId: string; vendorName: string }>();
    vendorProducts.forEach((vp) => {
      m.set(vp.id, { vendorId: vp.vendorId, vendorName: vp.vendorName ?? '—' });
    });
    return m;
  }, [vendorProducts]);

  const vpById = useMemo(() => {
    const m = new Map<string, VendorProduct>();
    vendorProducts.forEach((vp) => m.set(vp.id, vp));
    return m;
  }, [vendorProducts]);

  const vendorOptions = useMemo(() => {
    const byId = new Map<string, string>();
    vendorProducts.forEach((vp) => {
      if (vp.vendorId) byId.set(vp.vendorId, vp.vendorName ?? vp.vendorId);
    });
    return [
      { value: 'all', label: 'All vendors' },
      ...Array.from(byId, ([id, name]) => ({ value: id, label: name })).sort((a, b) =>
        a.label.localeCompare(b.label),
      ),
    ];
  }, [vendorProducts]);

  const rows = useMemo(() => {
    const q = productFilter.trim().toLowerCase();
    return mappings
      .filter((m) => {
        if (vendorFilter !== 'all') {
          const owner = vendorByVendorProduct.get(m.vendorProductId);
          if (owner?.vendorId !== vendorFilter) return false;
        }
        if (q) {
          const hay = [m.productName, m.productSku, m.vendorProductName, m.vendorProductSku]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          if (!hay.includes(q)) return false;
        }
        return true;
      })
      .sort((a, b) => (a.productName ?? '').localeCompare(b.productName ?? ''));
  }, [mappings, vendorFilter, productFilter, vendorByVendorProduct]);

  const mismatchCount = useMemo(() => rows.filter((r) => r.termMismatch === true).length, [rows]);

  // Active vendor products with no active mapping → no billing coverage. Uses
  // active mappings only (an inactive-only mapping still leaves it uncovered),
  // so it's correct regardless of the include-inactive toggle. Honours the same
  // vendor + search filters as the main table.
  const unmapped = useMemo(() => {
    const mappedVpIds = new Set(
      mappings.filter((m) => m.isActive).map((m) => m.vendorProductId),
    );
    const q = productFilter.trim().toLowerCase();
    return vendorProducts
      .filter((vp) => vp.isActive && !mappedVpIds.has(vp.id))
      .filter((vp) => vendorFilter === 'all' || vp.vendorId === vendorFilter)
      .filter((vp) => {
        if (!q) return true;
        return [vp.name, vp.sku, vp.vendorName]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(q);
      })
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
  }, [vendorProducts, mappings, vendorFilter, productFilter]);

  const openCreate = (vp?: VendorProduct) => {
    setDialogInitialVp(vp ?? null);
    setDialogTarget(null);
  };

  const closeDialog = () => {
    setDialogTarget(undefined);
    setDialogInitialVp(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteProductMapping(deleteTarget.id);
      setDeleteTarget(null);
      setToast('Mapping deleted');
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete mapping');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <SecondaryToolbar floating>
        <ToolbarSelect
          label="Vendor"
          value={vendorFilter}
          onChange={setVendorFilter}
          options={vendorOptions}
        />
        <ToolbarSearch
          searchValue={productFilter}
          onSearchChange={onProductFilterChange ?? (() => {})}
          searchPlaceholder="Filter product…"
          maxWidth={280}
        />
        <ToolbarCheckbox
          label="Include inactive"
          active={includeInactive}
          onClick={() => setIncludeInactive((v) => !v)}
        />
        <ToolbarSpacer />
        {canEdit && (
          <ToolbarButton variant="primary" icon={IconPlus} onClick={() => openCreate()}>
            Create mapping
          </ToolbarButton>
        )}
        <ToolbarButton icon={IconRefresh} onClick={() => load()}>
          Refresh
        </ToolbarButton>
      </SecondaryToolbar>

      <Box sx={{ p: '12px 16px 24px' }}>
        <Typography sx={{ fontSize: 12, color: '#888', mb: 1.5 }}>
          Commercial ⇄ vendor product mappings feeding billing coverage.
          {mismatchCount > 0 && (
            <Box component="span" sx={{ color: '#C07A10', fontWeight: 600 }}>
              {' '}
              {mismatchCount} term mismatch{mismatchCount === 1 ? '' : 'es'} flagged.
            </Box>
          )}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}

        {!loading && unmapped.length > 0 && (
          <Box sx={[CARD_SX, { overflow: 'hidden', mb: 1 }]}>
            <Box
              onClick={() => setUnmappedOpen((v) => !v)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: '10px 16px',
                cursor: 'pointer',
                borderBottom: unmappedOpen ? '0.5px solid #e2eaf3' : 'none',
                userSelect: 'none',
              }}
            >
              <Box component="span" sx={{ color: '#C07A10', display: 'flex' }}>
                <IconAlertTriangle size={16} stroke={1.5} />
              </Box>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>
                {unmapped.length} unmapped vendor product{unmapped.length === 1 ? '' : 's'}
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#888' }}>
                — no active mapping, so no billing coverage
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Box
                component="span"
                sx={{
                  color: '#bbb',
                  display: 'flex',
                  transform: unmappedOpen ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.15s ease',
                }}
              >
                <IconChevronDown size={16} stroke={1.5} />
              </Box>
            </Box>
            {unmappedOpen && (
              <DataTable>
                <thead>
                  <tr>
                    <DataTableHeadCell>Vendor product</DataTableHeadCell>
                    <DataTableHeadCell>Vendor</DataTableHeadCell>
                    <DataTableHeadCell>Term</DataTableHeadCell>
                    {canEdit && <DataTableHeadCell align="right">Action</DataTableHeadCell>}
                  </tr>
                </thead>
                <tbody>
                  {unmapped.map((vp) => (
                    <DataTableRow key={vp.id}>
                      <DataTableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Button variant="text" size="small" sx={LINK_BTN_SX} onClick={() => setDrillTarget(vp)}>
                            {vp.name ?? vp.sku}
                          </Button>
                          {vp.sku && vp.sku !== vp.name && (
                            <Typography sx={{ fontSize: 12, color: '#888' }}>{vp.sku}</Typography>
                          )}
                        </Box>
                      </DataTableCell>
                      <DataTableCell>{vp.vendorName ?? '—'}</DataTableCell>
                      <DataTableCell>
                        <Chip>{vp.billingTerm}</Chip>
                      </DataTableCell>
                      {canEdit && (
                        <DataTableCell align="right">
                          <Button
                            size="small"
                            variant="outlined"
                            sx={ACTION_BTN_SX}
                            startIcon={<IconLink size={14} stroke={1.5} />}
                            onClick={() => openCreate(vp)}
                          >
                            Map
                          </Button>
                        </DataTableCell>
                      )}
                    </DataTableRow>
                  ))}
                </tbody>
              </DataTable>
            )}
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Box sx={[CARD_SX, { overflow: 'hidden' }]}>
            <DataTable>
              <thead>
                <tr>
                  <DataTableHeadCell>Commercial product</DataTableHeadCell>
                  <DataTableHeadCell>Vendor</DataTableHeadCell>
                  <DataTableHeadCell>Vendor product</DataTableHeadCell>
                  <DataTableHeadCell align="right">Seat ×</DataTableHeadCell>
                  <DataTableHeadCell>Active</DataTableHeadCell>
                  {canEdit && <DataTableHeadCell align="right">Actions</DataTableHeadCell>}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <DataTableEmpty colSpan={canEdit ? 6 : 5}>
                    No mappings for the selected filters.
                  </DataTableEmpty>
                ) : (
                  rows.map((m) => {
                    const owner = vendorByVendorProduct.get(m.vendorProductId);
                    return (
                      <DataTableRow key={m.id}>
                        <DataTableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography sx={{ fontSize: 13, color: '#1a1a1a' }}>
                              {m.productName ?? '—'}
                            </Typography>
                            {m.productSku && m.productSku !== m.productName && (
                              <Typography sx={{ fontSize: 12, color: '#888' }}>{m.productSku}</Typography>
                            )}
                          </Box>
                        </DataTableCell>
                        <DataTableCell>{owner?.vendorName ?? '—'}</DataTableCell>
                        <DataTableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                              {vpById.has(m.vendorProductId) ? (
                                <Button
                                  variant="text"
                                  size="small"
                                  sx={LINK_BTN_SX}
                                  onClick={() => setDrillTarget(vpById.get(m.vendorProductId)!)}
                                >
                                  {m.vendorProductName ?? m.vendorProductSku ?? '—'}
                                </Button>
                              ) : (
                                <Typography sx={{ fontSize: 13, color: '#333' }}>
                                  {m.vendorProductName ?? m.vendorProductSku ?? '—'}
                                </Typography>
                              )}
                              {m.vendorProductBillingTerm && (
                                <Chip tone={m.termMismatch ? 'warning' : 'neutral'}>
                                  {m.vendorProductBillingTerm}
                                </Chip>
                              )}
                              {m.termMismatch && (
                                <Box
                                  component="span"
                                  sx={{ color: '#C07A10', display: 'flex' }}
                                  title="Term mismatch: the commercial SKU's term differs from the vendor product's billing term."
                                >
                                  <IconAlertTriangle size={15} stroke={1.5} />
                                </Box>
                              )}
                            </Box>
                            {m.vendorProductSku && m.vendorProductSku !== m.vendorProductName && (
                              <Typography sx={{ fontSize: 12, color: '#888' }}>
                                {m.vendorProductSku}
                              </Typography>
                            )}
                          </Box>
                        </DataTableCell>
                        <DataTableCell align="right">{m.seatMultiplier}</DataTableCell>
                        <DataTableCell>
                          <Chip tone={m.isActive ? 'success' : 'neutral'}>
                            {m.isActive ? 'active' : 'inactive'}
                          </Chip>
                        </DataTableCell>
                        {canEdit && (
                          <DataTableCell align="right">
                            <IconButton size="small" title="Edit" onClick={() => setDialogTarget(m)}>
                              <IconPencil size={16} stroke={1.5} />
                            </IconButton>
                            <IconButton size="small" title="Delete" onClick={() => setDeleteTarget(m)}>
                              <IconTrash size={16} stroke={1.5} />
                            </IconButton>
                          </DataTableCell>
                        )}
                      </DataTableRow>
                    );
                  })
                )}
              </tbody>
            </DataTable>
          </Box>
        )}
      </Box>

      {dialogTarget !== undefined && (
        <ProductMappingDialog
          open
          mapping={dialogTarget}
          initialVendorProduct={dialogInitialVp}
          onClose={closeDialog}
          onSaved={() => {
            closeDialog();
            setToast('Mapping saved');
            load();
          }}
        />
      )}

      {drillTarget && (
        <VendorProductDrillDownDialog
          open
          vendorProduct={drillTarget}
          canEdit={canEdit}
          onClose={() => setDrillTarget(null)}
          onMap={(vp) => {
            setDrillTarget(null);
            openCreate(vp);
          }}
          onChanged={() => {
            setDrillTarget(null);
            setToast('Vendor product updated');
            load();
          }}
        />
      )}

      <Dialog open={!!deleteTarget} onClose={deleting ? undefined : () => setDeleteTarget(null)}>
        <DialogTitle>Delete mapping?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Permanently delete the mapping
            {deleteTarget ? ` for "${deleteTarget.productName ?? deleteTarget.productSku}"` : ''}? This
            removes its contribution to billing coverage and can't be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={confirmDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        message={toast ?? ''}
      />
    </>
  );
};
